import React, { FC, useMemo } from 'react';
import { StyleProp, ViewStyle } from 'react-native';

import { EDataTestId } from '@webtrader/core/constants/test';
import { useRequestTimer } from '@webtrader/core/hooks/utils/use-request-timer';
import { useTranslation } from '@webtrader/core/i18n';
import signUpService, { useSignUp } from '@webtrader/core/services/sign-up';
import { isValidEmail } from '@webtrader/core/validators';

import { AlertManager } from '@/components/Alert/Manager';
import Button from '@/components/Button';

interface IRequestOtpButton {
  isFormSubmitting?: boolean;
  email?: string;
}

const BUTTON_STYLE: StyleProp<ViewStyle> = { textAlign: 'center' };

const RequestOtpButton: FC<IRequestOtpButton> = ({ isFormSubmitting, email }) => {
  const { t } = useTranslation();
  const { requestOtpLoading } = useSignUp();
  const { buttonText, startTimer, remainingTime } = useRequestTimer({
    timerPrefixText: t('Time Remaining'),
    requestText: t('Request OTP')
  });

  const isDisabled = useMemo(() => {
    return isFormSubmitting || remainingTime > 0 || !isValidEmail(email || '');
  }, [isFormSubmitting, remainingTime, email]);

  const onPress = async () => {
    if (!email) {
      return;
    }

    const { success, message } = await signUpService.requestOtp(email);
    if (success) {
      startTimer();
    } else {
      AlertManager().alert({
        type: 'error',
        message,
        absoluteAlert: true
      });
    }
  };

  return (
    <Button
      onPress={onPress}
      disabled={isDisabled}
      loading={requestOtpLoading}
      style={BUTTON_STYLE}
      testID={EDataTestId.SIGN_UP_OTP_BUTTON}
    >
      {buttonText}
    </Button>
  );
};

export default RequestOtpButton;
